import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DropdownArrow from '../components/DropdownArrow';
import { Container, Table, Button, Alert, Card, Badge, Form, Pagination } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { paymentService, accountHolderService, farmWorkService as expenseService, equipmentHiringService, trustAccountService } from '../services/apiClient';
import { PaymentSummary, AccountHolder, FarmWork, EquipmentHiring, Payment, TrustAccount } from '../types/index';
import { useLocalization } from '../context/LocalizationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingIndicator from '../components/LoadingIndicator';
import { getActiveSessionMonth, isSameMonth, setActiveSessionMonth, subscribeToActiveSessionMonth } from '../utils/sessionMonth';
import { getSavedTrustAccountId, isSavingsTrustAccount, sortTrustAccountsByKind } from '../utils/trustAccount';
import './Statements.css';

interface HolderStatementRow {
  accountHolderId: number;
  accountHolderName: string;
  accountHolderEmail: string;
  paymentSummary?: PaymentSummary;
  addressFees: HolderAddressFeeBreakdown[];
  holderCredits: number;
  totalDebits: number;
  requiredContribution: number;
  monthlyAccountFees: number;
  totalRequiredContribution: number;
  allocatedToFarmWork: number;
  paidFromStatements: number;
  netStatementMovement: number;
  outstandingFees: number;
  overpaidAmount: number;
  contributionItemCount: number;
}

interface HolderAddressFeeBreakdown {
  address: string;
  monthlyFee: number;
}

interface ExpenseOverviewRow {
  expenseId: number;
  expenseTypeName: string;
  description: string;
  contributorsCount: number;
  cost: number;
  expectedContribution: number;
  estimatedPaid: number;
  outstandingContribution: number;
  costCoverageGap: number;
  unpaidContributors: number;
  partiallyPaidContributors: number;
  paidInFullContributors: number;
}

interface HolderExpenseAllocation {
  expenseId: number;
  expenseTypeName: string;
  description: string;
  requiredContribution: number;
  allocatedPaid: number;
  outstandingContribution: number;
}

interface StatementExpenseContribution {
  accountHolderId: number;
  amountContributed: number;
}

interface StatementExpenseIncomeRecipient {
  accountHolderId: number;
  incomeShare: number;
}

interface StatementExpenseItem {
  id: number;
  expenseTypeName: string;
  description: string;
  cost: number;
  createdDate: string;
  contributions: StatementExpenseContribution[];
  incomeRecipients: StatementExpenseIncomeRecipient[];
}

const getPreviousMonth = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 1, 1);
};

const normalizeMonthStart = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

const getDisplayAccountType = (accountKind: string | undefined, t: (key: string) => string): string => {
  if (!accountKind) return '-';
  return accountKind.trim().toLowerCase() === 'savings' ? t('savingsAccountType') : t('currentAccountType');
};

const isHolderTrackableForMonth = (holder: AccountHolder | undefined, month: Date): boolean => {
  if (!holder) return true;
  if (holder.isActive) return true;

  if (!holder.deactivatedDate) return false;
  const deactivated = new Date(holder.deactivatedDate);
  if (Number.isNaN(deactivated.getTime())) return false;

  const targetMonthStart = new Date(month.getFullYear(), month.getMonth(), 1).getTime();
  const deactivationMonthStart = new Date(deactivated.getFullYear(), deactivated.getMonth(), 1).getTime();
  return targetMonthStart < deactivationMonthStart;
};

const normalizeHolderAddressFees = (holder?: AccountHolder, forMonth?: Date): HolderAddressFeeBreakdown[] => {
  if (!holder) return [];

  if (forMonth && Array.isArray(holder.monthlyFeeHistory) && holder.monthlyFeeHistory.length > 0) {
    const targetMonth = new Date(forMonth.getFullYear(), forMonth.getMonth(), 1).getTime();
    const effectiveHistory = holder.monthlyFeeHistory
      .map((entry) => ({
        ...entry,
        effectiveFrom: new Date(entry.effectiveFromMonth),
        changedAt: new Date(entry.changedAt),
      }))
      .filter((entry) => !Number.isNaN(entry.effectiveFrom.getTime()))
      .sort((first, second) => {
        const effectiveDelta = first.effectiveFrom.getTime() - second.effectiveFrom.getTime();
        if (effectiveDelta !== 0) return effectiveDelta;
        return first.changedAt.getTime() - second.changedAt.getTime();
      })
      .filter((entry) => entry.effectiveFrom.getTime() <= targetMonth)
      .pop();

    if (effectiveHistory && Array.isArray(effectiveHistory.addressFees)) {
      const historyAddressFees = effectiveHistory.addressFees
        .map((item) => ({
          address: (item.address || '').trim(),
          monthlyFee: Number(item.monthlyFee) || 0,
        }))
        .filter((item) => !!item.address);

      if (historyAddressFees.length > 0) {
        return historyAddressFees;
      }
    }
  }

  const distributeMonthlyFee = (addresses: string[], totalMonthlyFee: number): HolderAddressFeeBreakdown[] => {
    if (addresses.length === 0) return [];
    const normalizedTotal = Math.max(0, Number(totalMonthlyFee) || 0);
    if (normalizedTotal <= 0) {
      return addresses.map((address) => ({ address, monthlyFee: 0 }));
    }

    const cents = Math.round(normalizedTotal * 100);
    const baseCents = Math.floor(cents / addresses.length);
    const remainder = cents % addresses.length;

    return addresses.map((address, index) => ({
      address,
      monthlyFee: (baseCents + (index < remainder ? 1 : 0)) / 100,
    }));
  };

  const normalizedFromAddressFees = (holder.addressFees || [])
    .map((item) => ({
      address: (item.address || '').trim(),
      monthlyFee: Number(item.monthlyFee) || 0,
    }))
    .filter((item) => !!item.address);

  if (normalizedFromAddressFees.length > 0) {
    const normalizedFromAddressFeesTotal = normalizedFromAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0);
    const fallbackMonthlyFee = Number(holder.monthlyFee) || 0;

    if (normalizedFromAddressFeesTotal <= 0 && fallbackMonthlyFee > 0) {
      return distributeMonthlyFee(
        normalizedFromAddressFees.map((item) => item.address),
        fallbackMonthlyFee
      );
    }

    return normalizedFromAddressFees;
  }

  const fallbackAddresses = Array.from(new Set([
    ...(holder.addresses || []),
    holder.address || '',
  ].map((address) => (address || '').trim()).filter((address) => !!address)));

  const fallbackMonthlyFee = Number(holder.monthlyFee) || 0;
  return distributeMonthlyFee(fallbackAddresses, fallbackMonthlyFee);
};

const buildEquipmentExpenseDescription = (item: EquipmentHiring): string => {
  const equipment = (item.equipmentDescription || '').trim();
  const description = (item.description || '').trim();

  if (equipment && description) {
    return `${equipment} - ${description}`;
  }

  return equipment || description || 'Equipment Hiring';
};

const getFriendlyStatementSendErrorMessage = (err: unknown, fallbackMessage: string): string => {
  const errorData = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  const rawError = `${errorData?.error || ''} ${errorData?.message || ''}`.trim();

  if (!rawError) {
    return fallbackMessage;
  }

  const normalized = rawError.toLowerCase();
  const isUnverifiedEmailError = normalized.includes('email address is not verified')
    || (normalized.includes('message rejected') && normalized.includes('identities failed'));

  if (!isUnverifiedEmailError) {
    return fallbackMessage;
  }

  const failedIdentitiesMatch = /identities failed[^:]*:\s*(.+)$/i.exec(rawError);
  const failedIdentities = failedIdentitiesMatch?.[1]
    ?.split(/[;,]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0) || [];

  if (failedIdentities.length > 0) {
    return `Statement could not be sent because one or more recipient emails are not verified: ${failedIdentities.join(', ')}. Please verify these addresses in Amazon SES or update the account holder email addresses.`;
  }

  return 'Statement could not be sent because one or more recipient emails are not verified in Amazon SES. Please verify the email address or update the account holder email.';
};

interface StatementsProps {
  compact?: boolean;
}

export const Statements: React.FC<StatementsProps> = ({ compact = false }) => {
  const { t, formatCurrency, formatDate, formatMonthYear, language } = useLocalization();
  const tRef = useRef(t);
  const selectAllContributorsRef = useRef<HTMLInputElement | null>(null);
  const initialSelectedMonthRef = useRef<Date>(getActiveSessionMonth(normalizeMonthStart(getPreviousMonth())));
  const [summaries, setSummaries] = useState<PaymentSummary[]>([]);
  const [expenseItems, setExpenseItems] = useState<StatementExpenseItem[]>([]);
  const [accountHolders, setAccountHolders] = useState<AccountHolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingStatements, setSendingStatements] = useState<Set<number>>(new Set());
  const [selectedContributorIds, setSelectedContributorIds] = useState<Set<number>>(new Set());
  const [selectedAccountHolderId, setSelectedAccountHolderId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ variant: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [showSendSelectedConfirm, setShowSendSelectedConfirm] = useState(false);
  const [pendingPrint, setPendingPrint] = useState(false);
  const [printSelectionIds, setPrintSelectionIds] = useState<number[]>([]);
  const [sendingSelected, setSendingSelected] = useState(false);
  const [filterAccountId, setFilterAccountId] = useState<number | undefined>();
  const [expenseOverviewCurrentPage, setExpenseOverviewCurrentPage] = useState(1);
  const [expenseOverviewPageSize, setExpenseOverviewPageSize] = useState(10);
  const [holderOverviewCurrentPage, setHolderOverviewCurrentPage] = useState(1);
  const [holderOverviewPageSize, setHolderOverviewPageSize] = useState(10);
  const [selectedMonth, setSelectedMonth] = useState<Date>(initialSelectedMonthRef.current);
  const [debouncedSelectedMonth, setDebouncedSelectedMonth] = useState<Date>(initialSelectedMonthRef.current);
  const [dashboardSelectedTrustAccountId, setDashboardSelectedTrustAccountId] = useState<number | null>(getSavedTrustAccountId());
  const [selectedTrustAccount, setSelectedTrustAccount] = useState<TrustAccount | null>(null);
  const [savingsTransactions, setSavingsTransactions] = useState<Payment[]>([]);

  const isSavingsSelected = useMemo(() => isSavingsTrustAccount(selectedTrustAccount), [selectedTrustAccount]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    return subscribeToActiveSessionMonth((nextMonth) => {
      setSelectedMonth((previousMonth) => (isSameMonth(previousMonth, nextMonth) ? previousMonth : nextMonth));
    });
  }, []);

  useEffect(() => {
    setActiveSessionMonth(selectedMonth);
  }, [selectedMonth]);

  const clearPrintState = useCallback(() => {
    setPendingPrint(false);
    setSelectedAccountHolderId(null);
    setPrintSelectionIds([]);
  }, []);

  useEffect(() => {
    const syncDashboardTrustAccount = () => {
      setDashboardSelectedTrustAccountId(getSavedTrustAccountId());
    };

    window.addEventListener('trust-account-changed', syncDashboardTrustAccount);
    window.addEventListener('storage', syncDashboardTrustAccount);

    return () => {
      window.removeEventListener('trust-account-changed', syncDashboardTrustAccount);
      window.removeEventListener('storage', syncDashboardTrustAccount);
    };
  }, []);

  useEffect(() => {
    const handleAfterPrint = () => {
      clearPrintState();
    };

    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [clearPrintState]);

  const getMonthRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return {
      start: new Date(`${start.toISOString().split('T')[0]}T00:00:00`).toISOString(),
      end: new Date(`${end.toISOString().split('T')[0]}T23:59:59`).toISOString(),
    };
  };

  const paymentSummaryByHolderId = useMemo(() => {
    const map = new Map<number, PaymentSummary>();
    summaries.forEach((summary) => {
      map.set(summary.accountHolderId, summary);
    });
    return map;
  }, [summaries]);

  const accountHolderById = useMemo(() => {
    const map = new Map<number, AccountHolder>();
    accountHolders.forEach((holder) => {
      map.set(holder.id, holder);
    });
    return map;
  }, [accountHolders]);

  const holderAllocationMap = useMemo(() => {
    const holderIds = new Set<number>();

    summaries.forEach((summary) => {
      const holder = accountHolderById.get(summary.accountHolderId);
      if (!isHolderTrackableForMonth(holder, debouncedSelectedMonth)) {
        return;
      }

      holderIds.add(summary.accountHolderId);
    });

    accountHolders.forEach((holder) => {
      if (!isHolderTrackableForMonth(holder, debouncedSelectedMonth)) {
        return;
      }

      const holderCredits = (holder.creditNotes || []).reduce(
        (sum, note) => sum + Math.max(note.amount || 0, 0),
        0
      );
      if (holderCredits > 0) {
        holderIds.add(holder.id);
      }

      const monthlyAccountFees = normalizeHolderAddressFees(holder, debouncedSelectedMonth).reduce((sum, item) => sum + item.monthlyFee, 0);
      if (monthlyAccountFees > 0) {
        holderIds.add(holder.id);
      }
    });

    expenseItems.forEach((work) => {
      work.contributions.forEach((contribution) => {
        const holder = accountHolderById.get(contribution.accountHolderId);
        if (!isHolderTrackableForMonth(holder, debouncedSelectedMonth)) {
          return;
        }

        holderIds.add(contribution.accountHolderId);
      });

      work.incomeRecipients.forEach((recipient) => {
        const holder = accountHolderById.get(recipient.accountHolderId);
        if (!isHolderTrackableForMonth(holder, debouncedSelectedMonth)) {
          return;
        }

        holderIds.add(recipient.accountHolderId);
      });
    });

    const allocationsByHolder = new Map<number, HolderExpenseAllocation[]>();
    const rowsByHolder = new Map<number, HolderStatementRow>();

    holderIds.forEach((holderId) => {
      const summary = paymentSummaryByHolderId.get(holderId);
      const holder = accountHolderById.get(holderId);
      if (!isHolderTrackableForMonth(holder, debouncedSelectedMonth)) {
        return;
      }

      const addressFees = normalizeHolderAddressFees(holder, debouncedSelectedMonth);
      const monthlyAccountFees = addressFees.reduce((sum, item) => sum + item.monthlyFee, 0);
      const holderCredits = (holder?.creditNotes || []).reduce(
        (sum, note) => sum + Math.max(note.amount || 0, 0),
        0
      );
      const totalDebits = (summary?.payments || []).reduce(
        (sum, payment) => sum + Math.max(-payment.amount, 0),
        0
      );
      const netStatementMovement = (summary?.payments || []).reduce(
        (sum, payment) => sum + payment.amount,
        0
      ) + holderCredits;

      const holderObligations = expenseItems
        .map((work) => {
          const contribution = work.contributions.find((item) => item.accountHolderId === holderId);
          const recipient = work.incomeRecipients.find((item) => item.accountHolderId === holderId);
          if (!contribution && !recipient) return null;

          const creditedSuffix = recipient && !contribution
            ? ` (Credited: ${formatCurrency(recipient.incomeShare)})`
            : '';

          return {
            expenseId: work.id,
            expenseTypeName: work.expenseTypeName,
            description: `${work.description}${creditedSuffix}`,
            requiredContribution: contribution?.amountContributed ?? 0,
            createdDate: work.createdDate,
          };
        })
        .filter((item): item is NonNullable<typeof item> => !!item)
        .sort((first, second) => {
          const firstDate = new Date(first.createdDate).getTime();
          const secondDate = new Date(second.createdDate).getTime();
          if (firstDate !== secondDate) return firstDate - secondDate;
          return first.expenseId - second.expenseId;
        });

      let remainingPaid = Math.max(holderCredits, 0);
      const allocationRows: HolderExpenseAllocation[] = holderObligations.map((obligation) => {
        const allocatedPaid = Math.min(obligation.requiredContribution, remainingPaid);
        remainingPaid = Math.max(remainingPaid - allocatedPaid, 0);

        return {
          expenseId: obligation.expenseId,
          expenseTypeName: obligation.expenseTypeName,
          description: obligation.description,
          requiredContribution: obligation.requiredContribution,
          allocatedPaid,
          outstandingContribution: Math.max(obligation.requiredContribution - allocatedPaid, 0),
        };
      });

      const requiredContribution = allocationRows.reduce((sum, row) => sum + row.requiredContribution, 0);
      const totalRequiredContribution = requiredContribution + monthlyAccountFees;
      const allocatedPaidTotal = allocationRows.reduce((sum, row) => sum + row.allocatedPaid, 0);
      const outstandingFees = Math.max(totalRequiredContribution - holderCredits, 0);
      const paidFromStatements = Math.max(holderCredits, 0);
      const overpaidAmount = Math.max(paidFromStatements - totalRequiredContribution, 0);

      allocationsByHolder.set(holderId, allocationRows);
      rowsByHolder.set(holderId, {
        accountHolderId: holderId,
        accountHolderName: summary?.accountHolderName || holder?.name || t('unknown'),
        accountHolderEmail: summary?.accountHolderEmail || holder?.email || '-',
        paymentSummary: summary,
        addressFees,
        holderCredits,
        totalDebits,
        requiredContribution,
        monthlyAccountFees,
        totalRequiredContribution,
        allocatedToFarmWork: allocatedPaidTotal,
        paidFromStatements,
        netStatementMovement,
        outstandingFees,
        overpaidAmount,
        contributionItemCount: allocationRows.length,
      });
    });

    return { allocationsByHolder, rowsByHolder };
  }, [summaries, accountHolders, expenseItems, paymentSummaryByHolderId, accountHolderById, debouncedSelectedMonth, t, formatCurrency]);

  const holderStatementRows = useMemo<HolderStatementRow[]>(() => {
    const allRows = Array.from(holderAllocationMap.rowsByHolder.values());
    const filtered = filterAccountId
      ? allRows.filter((row) => row.accountHolderId === filterAccountId)
      : allRows;

    return filtered.sort((first, second) => {
      if (second.outstandingFees !== first.outstandingFees) {
        return second.outstandingFees - first.outstandingFees;
      }

      return first.accountHolderName.localeCompare(second.accountHolderName);
    });
  }, [holderAllocationMap.rowsByHolder, filterAccountId]);

  const expenseOverviewRows = useMemo<ExpenseOverviewRow[]>(() => {
    return expenseItems.map((work) => {
      const allContributions = work.contributions;
      const expectedContribution = allContributions.reduce((sum, contribution) => sum + contribution.amountContributed, 0);

      let unpaidContributors = 0;
      let partiallyPaidContributors = 0;
      let paidInFullContributors = 0;

      const estimatedPaid = allContributions.reduce((sum, contribution) => {
        const holderAllocations = holderAllocationMap.allocationsByHolder.get(contribution.accountHolderId) || [];
        const workAllocation = holderAllocations.find((allocation) => allocation.expenseId === work.id);

        const allocatedPaid = workAllocation?.allocatedPaid ?? 0;
        const requiredContribution = contribution.amountContributed;
        const outstandingContribution = Math.max(requiredContribution - allocatedPaid, 0);

        if (allocatedPaid <= 0 && requiredContribution > 0) {
          unpaidContributors += 1;
        } else if (outstandingContribution > 0) {
          partiallyPaidContributors += 1;
        } else {
          paidInFullContributors += 1;
        }

        return sum + (workAllocation?.allocatedPaid ?? 0);
      }, 0);

      return {
        expenseId: work.id,
        expenseTypeName: work.farmWorkTypeName,
        description: work.description,
        contributorsCount: allContributions.length,
        cost: work.cost,
        expectedContribution,
        estimatedPaid,
        outstandingContribution: Math.max(expectedContribution - estimatedPaid, 0),
        costCoverageGap: Math.max(work.cost - expectedContribution, 0),
        unpaidContributors,
        partiallyPaidContributors,
        paidInFullContributors,
      };
    });
  }, [expenseItems, holderAllocationMap.allocationsByHolder]);

  const holderSummaryTotals = useMemo(() => {
    return holderStatementRows.reduce(
      (totals, row) => {
        totals.requiredContribution += row.requiredContribution;
        totals.monthlyAccountFees += row.monthlyAccountFees;
        totals.totalRequiredContribution += row.totalRequiredContribution;
        totals.holderCredits += row.holderCredits;
        totals.totalDebits += row.totalDebits;
        totals.allocatedToFarmWork += row.allocatedToFarmWork;
        totals.outstandingFees += row.outstandingFees;
        totals.withOutstanding += row.outstandingFees > 0 ? 1 : 0;
        totals.paidInFull += row.requiredContribution > 0 && row.outstandingFees === 0 ? 1 : 0;
        totals.notPaid += row.requiredContribution > 0 && row.holderCredits <= 0 ? 1 : 0;
        return totals;
      },
      {
        requiredContribution: 0,
        monthlyAccountFees: 0,
        totalRequiredContribution: 0,
        holderCredits: 0,
        totalDebits: 0,
        allocatedToFarmWork: 0,
        outstandingFees: 0,
        withOutstanding: 0,
        paidInFull: 0,
        notPaid: 0,
      }
    );
  }, [holderStatementRows]);

  const expenseOverviewTotalPages = Math.max(1, Math.ceil(expenseOverviewRows.length / expenseOverviewPageSize));
  const safeExpenseOverviewCurrentPage = Math.min(expenseOverviewCurrentPage, expenseOverviewTotalPages);
  const pagedExpenseOverviewRows = useMemo(() => {
    const startIndex = (safeExpenseOverviewCurrentPage - 1) * expenseOverviewPageSize;
    return expenseOverviewRows.slice(startIndex, startIndex + expenseOverviewPageSize);
  }, [expenseOverviewRows, safeExpenseOverviewCurrentPage, expenseOverviewPageSize]);
  const expenseOverviewPageStart = expenseOverviewRows.length === 0 ? 0 : (safeExpenseOverviewCurrentPage - 1) * expenseOverviewPageSize + 1;
  const expenseOverviewPageEnd = Math.min(safeExpenseOverviewCurrentPage * expenseOverviewPageSize, expenseOverviewRows.length);

  const holderOverviewTotalPages = Math.max(1, Math.ceil(holderStatementRows.length / holderOverviewPageSize));
  const safeHolderOverviewCurrentPage = Math.min(holderOverviewCurrentPage, holderOverviewTotalPages);
  const pagedHolderOverviewRows = useMemo(() => {
    const startIndex = (safeHolderOverviewCurrentPage - 1) * holderOverviewPageSize;
    return holderStatementRows.slice(startIndex, startIndex + holderOverviewPageSize);
  }, [holderStatementRows, safeHolderOverviewCurrentPage, holderOverviewPageSize]);
  const holderOverviewPageStart = holderStatementRows.length === 0 ? 0 : (safeHolderOverviewCurrentPage - 1) * holderOverviewPageSize + 1;
  const holderOverviewPageEnd = Math.min(safeHolderOverviewCurrentPage * holderOverviewPageSize, holderStatementRows.length);

  const filteredContributorIds = useMemo(() => holderStatementRows.map((row) => row.accountHolderId), [holderStatementRows]);

  const selectedContributorCount = useMemo(() => {
    if (selectedContributorIds.size === 0) return 0;
    const filteredSet = new Set(filteredContributorIds);
    let count = 0;
    selectedContributorIds.forEach((id) => {
      if (filteredSet.has(id)) {
        count += 1;
      }
    });
    return count;
  }, [selectedContributorIds, filteredContributorIds]);

  const areAllFilteredContributorsSelected = filteredContributorIds.length > 0
    && selectedContributorCount === filteredContributorIds.length;
  const hasPartiallySelectedContributors = selectedContributorCount > 0
    && selectedContributorCount < filteredContributorIds.length;

  const selectedAccountHolderLabel = filterAccountId
    ? accountHolders.find((holder) => holder.id === filterAccountId)?.name ?? t('unknown')
    : t('allAccountHolders');

  const selectedHolderStatement = useMemo(() => {
    if (!selectedAccountHolderId) return null;
    return holderStatementRows.find((row) => row.accountHolderId === selectedAccountHolderId) || null;
  }, [selectedAccountHolderId, holderStatementRows]);

  const selectedHolderStatementsForPrint = useMemo(() => {
    if (printSelectionIds.length === 0) {
      return [] as HolderStatementRow[];
    }

    const selectedSet = new Set(printSelectionIds);
    return holderStatementRows.filter((row) => selectedSet.has(row.accountHolderId));
  }, [holderStatementRows, printSelectionIds]);

  useEffect(() => {
    const hasPrintableSelection = selectedHolderStatementsForPrint.length > 0;
    if (!pendingPrint || (!selectedHolderStatement && !hasPrintableSelection)) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      let cleared = false;
      const clearOnce = () => {
        if (cleared) return;
        cleared = true;
        clearPrintState();
      };

      // Some browsers do not reliably fire afterprint. Focus usually returns
      // to the window once the print preview/dialog is closed.
      const handleWindowFocus = () => {
        clearOnce();
      };

      window.addEventListener('focus', handleWindowFocus, { once: true });

      try {
        window.print();
      } catch (error) {
        clearOnce();
        throw error;
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [pendingPrint, selectedHolderStatement, selectedHolderStatementsForPrint, clearPrintState]);

  const loadStatements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const trustAccountsData = sortTrustAccountsByKind(await trustAccountService.getAll());

      const activeTrustAccount = (dashboardSelectedTrustAccountId
        ? trustAccountsData.find((account) => account.id === dashboardSelectedTrustAccountId)
        : undefined)
        ?? trustAccountsData[0]
        ?? null;

      setSelectedTrustAccount((previous) => {
        if (previous?.id === activeTrustAccount?.id) {
          return previous;
        }

        return activeTrustAccount;
      });

      const selectedMonthRange = getMonthRange(debouncedSelectedMonth);

      if (activeTrustAccount && isSavingsTrustAccount(activeTrustAccount)) {
        const trustTransactions = await paymentService.getTrustTransactions(
          activeTrustAccount.id,
          selectedMonthRange.start,
          selectedMonthRange.end
        );

        setSavingsTransactions(trustTransactions);
        setSummaries([]);
        setAccountHolders([]);
        setExpenseItems([]);
        return;
      }

      const [summariesData, holdersData, farmWorkExpenseData, equipmentExpenseData] = await Promise.all([
        paymentService.getSummaries(selectedMonthRange.start, selectedMonthRange.end),
        accountHolderService.getAll(),
        expenseService.getByMonthYear(debouncedSelectedMonth.getMonth() + 1, debouncedSelectedMonth.getFullYear()),
        equipmentHiringService.getByMonthYear(debouncedSelectedMonth.getMonth() + 1, debouncedSelectedMonth.getFullYear()),
      ]);

      const normalizedFarmWorkExpenses: StatementExpenseItem[] = farmWorkExpenseData.map((work: FarmWork) => ({
        id: work.id,
        expenseTypeName: work.farmWorkTypeName,
        description: work.description,
        cost: work.cost,
        createdDate: work.createdDate,
        contributions: (work.contributions || []).map((contribution) => ({
          accountHolderId: contribution.accountHolderId,
          amountContributed: contribution.amountContributed,
        })),
        incomeRecipients: [],
      }));

      const normalizedEquipmentExpenses: StatementExpenseItem[] = equipmentExpenseData.map((item: EquipmentHiring) => ({
        // Use negative IDs so farm work and equipment rows never collide in allocation lookups.
        id: -item.id,
        expenseTypeName: 'Equipment Hiring',
        description: buildEquipmentExpenseDescription(item),
        cost: item.cost,
        createdDate: item.hiringDate || item.createdDate,
        contributions: (item.contributions || []).map((contribution) => ({
          accountHolderId: contribution.accountHolderId,
          amountContributed: contribution.amountContributed,
        })),
        incomeRecipients: (item.incomeRecipients || []).map((recipient) => ({
          accountHolderId: recipient.accountHolderId,
          incomeShare: recipient.incomeShare,
        })),
      }));

      setSavingsTransactions([]);
      setSummaries(summariesData);
      setAccountHolders(holdersData);
      setExpenseItems([...normalizedFarmWorkExpenses, ...normalizedEquipmentExpenses]);
    } catch (err) {
      setError(tRef.current('failedToLoadPaymentSummaries'));
      console.error(err);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [debouncedSelectedMonth, dashboardSelectedTrustAccountId]);

  useEffect(() => {
    void loadStatements();
  }, [loadStatements]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSelectedMonth((previousMonth) => {
        if (
          previousMonth.getFullYear() === selectedMonth.getFullYear()
          && previousMonth.getMonth() === selectedMonth.getMonth()
        ) {
          return previousMonth;
        }

        return selectedMonth;
      });
    }, 150);

    return () => clearTimeout(debounceTimer);
  }, [selectedMonth]);

  useEffect(() => {
    if (!selectedAccountHolderId) return;

    const existsInFilteredResults = holderStatementRows.some(
      (row) => row.accountHolderId === selectedAccountHolderId
    );

    if (!existsInFilteredResults) {
      setSelectedAccountHolderId(null);
    }
  }, [holderStatementRows, selectedAccountHolderId]);

  useEffect(() => {
    setSelectedAccountHolderId(null);
  }, [debouncedSelectedMonth]);

  useEffect(() => {
    setSelectedContributorIds((previous) => {
      if (previous.size === 0) return previous;

      const filteredSet = new Set(filteredContributorIds);
      const next = new Set<number>();
      previous.forEach((id) => {
        if (filteredSet.has(id)) {
          next.add(id);
        }
      });

      return next.size === previous.size ? previous : next;
    });
  }, [filteredContributorIds]);

  useEffect(() => {
    setSelectedContributorIds(new Set());
  }, [debouncedSelectedMonth, filterAccountId]);

  useEffect(() => {
    if (selectAllContributorsRef.current) {
      selectAllContributorsRef.current.indeterminate = hasPartiallySelectedContributors;
    }
  }, [hasPartiallySelectedContributors]);

  useEffect(() => {
    setExpenseOverviewCurrentPage(1);
  }, [debouncedSelectedMonth, expenseOverviewPageSize]);

  useEffect(() => {
    setHolderOverviewCurrentPage(1);
  }, [debouncedSelectedMonth, filterAccountId, holderOverviewPageSize]);

  const handleContributorSelection = (accountHolderId: number, isSelected: boolean) => {
    setSelectedContributorIds((previous) => {
      const next = new Set(previous);
      if (isSelected) {
        next.add(accountHolderId);
      } else {
        next.delete(accountHolderId);
      }
      return next;
    });
  };

  const handleToggleSelectAllContributors = (isSelected: boolean) => {
    setSelectedContributorIds((previous) => {
      const next = new Set(previous);

      if (isSelected) {
        filteredContributorIds.forEach((id) => next.add(id));
      } else {
        filteredContributorIds.forEach((id) => next.delete(id));
      }

      return next;
    });
  };

  const handleSendSelectedStatements = async () => {
    const holderIdsToSend = filteredContributorIds.filter((id) => selectedContributorIds.has(id));
    if (holderIdsToSend.length === 0) {
      setFeedback({ variant: 'danger', message: t('selectContributorBeforeSending') });
      return;
    }

    try {
      const selectedMonthRange = getMonthRange(debouncedSelectedMonth);

      setSendingSelected(true);
      setSendingStatements(new Set(holderIdsToSend));
      setFeedback({ variant: 'info', message: t('sendingStatements') });

      const promises = holderIdsToSend.map((holderId) =>
        paymentService.sendStatement(
          holderId,
          selectedMonthRange.start,
          selectedMonthRange.end,
          selectedTrustAccount?.id
        )
      );

      const results = await Promise.allSettled(promises);

      const successfulHolderCount = results.filter((result) => {
        if (result.status !== 'fulfilled') return false;
        return Boolean((result.value as { success?: boolean }).success);
      }).length;

      const failedHolderMessages: string[] = [];
      const skippedRecipients: string[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const payload = result.value as {
            success?: boolean;
            message?: string;
            failedRecipients?: string[];
          };

          if (!payload.success) {
            if (payload.message) {
              failedHolderMessages.push(payload.message);
            } else {
              failedHolderMessages.push(t('failedToSendStatement'));
            }
            return;
          }

          if (Array.isArray(payload.failedRecipients) && payload.failedRecipients.length > 0) {
            skippedRecipients.push(...payload.failedRecipients);
          }
          return;
        }

        const rejectedReason = result.reason as { response?: { data?: { error?: string; message?: string } } };
        const rawMessage = rejectedReason?.response?.data?.error
          || rejectedReason?.response?.data?.message
          || '';
        const friendlyMessage = getFriendlyStatementSendErrorMessage(result.reason, t('failedToSendStatement'));
        failedHolderMessages.push(rawMessage || friendlyMessage);
      });

      const uniqueSkippedRecipients = Array.from(new Set(skippedRecipients));

      if (failedHolderMessages.length > 0) {
        const combinedErrors = failedHolderMessages.join(' ');
        setFeedback({
          variant: 'danger',
          message: combinedErrors,
        });
      } else if (uniqueSkippedRecipients.length > 0) {
        setFeedback({
          variant: 'danger',
          message: `Statements were sent to valid recipients, but these email addresses were skipped because they are invalid or unverified: ${uniqueSkippedRecipients.join(', ')}.`,
        });
      } else if (successfulHolderCount > 0) {
        setFeedback({ variant: 'success', message: t('statementSentSuccessfully') });
      } else {
        setFeedback({ variant: 'danger', message: t('failedToSendStatements') });
      }

      await loadStatements();
    } catch (err) {
      setFeedback({
        variant: 'danger',
        message: getFriendlyStatementSendErrorMessage(err, t('failedToSendStatements')),
      });
      console.error(err);
    } finally {
      setSendingSelected(false);
      setSendingStatements(new Set());
      setShowSendSelectedConfirm(false);
    }
  };

  const handlePrintSelectedStatements = () => {
    const holderIdsToPrint = filteredContributorIds.filter((id) => selectedContributorIds.has(id));
    if (holderIdsToPrint.length === 0) {
      setFeedback({ variant: 'danger', message: t('selectContributorBeforePrinting') });
      return;
    }

    setPrintSelectionIds(holderIdsToPrint);
    setSelectedAccountHolderId(holderIdsToPrint.length === 1 ? holderIdsToPrint[0] : null);
    setPendingPrint(true);
  };

  if (loading && !hasLoadedOnce) {
    return (
      <Container className="py-5 page-loading-shell">
        <div className="page-loading-header">
          <div className="page-loading-title page-skeleton-shimmer" aria-hidden="true" />
          <div className="page-loading-subtitle">
            <LoadingIndicator label={t('loading')} className="system-loading-status-card-inline" showLines={false} />
          </div>
        </div>
        <div className="row g-4">
          <div className="col-lg-4 col-md-6">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-lg-8 col-md-6">
            <div className="page-loading-block page-loading-block-tall page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-12">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
        </div>
      </Container>
    );
  }

  if (isSavingsSelected) {
    const savingsIncome = savingsTransactions.filter((tx) => tx.amount > 0).reduce((sum, tx) => sum + tx.amount, 0);
    const savingsExpenses = savingsTransactions.filter((tx) => tx.amount < 0).reduce((sum, tx) => sum + tx.amount, 0);
    const savingsInterestGain = savingsTransactions
      .filter((tx) => (tx.description || '').toLowerCase().includes('interest'))
      .reduce((sum, tx) => sum + tx.amount, 0);

    return (
      <Container className={`py-5 statements-page${compact ? ' statements-page-compact' : ''}`} aria-busy={loading || sendingSelected || sendingStatements.size > 0}>
        {!compact && (
          <div className="statements-hero page-header-panel">
            <div className="statements-header d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-1 page-header-title">
                  <span className="page-header-title-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                      <path d="M7 3.5h7.5L19.5 8v10A2.5 2.5 0 0 1 17 20.5H7A2.5 2.5 0 0 1 4.5 18V6A2.5 2.5 0 0 1 7 3.5z" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
                      <path d="M14.5 3.8V8h4.2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
                      <path d="M8 11.2h8M8 14.5h8M8 17.8h5.3" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                    </svg>
                  </span>
                  {t('statements')}
                </h1>
              </div>
            </div>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="statements-tab-panel">
          <Card className="statements-filter-card dashboard-card dashboard-filter-panel mb-4">
            <Card.Body>
              <div className="row g-3 align-items-end">
                <div className="col-md-10">
                    <label className="form-label">{t('date')}</label>
                  <div className="app-date-picker-shell">
                    <DatePicker
                      selected={selectedMonth}
                      onChange={(date: Date | null) => {
                        if (!date) return;
                        setSelectedMonth(normalizeMonthStart(date));
                      }}
                      onChangeRaw={(e) => e.preventDefault()}
                      onKeyDown={(e) => e.preventDefault()}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      showFullMonthYearPicker
                      locale={language === 'af' ? 'af' : 'en-GB'}
                      className="form-control"
                      disabled={loading}
                      placeholderText={t('selectMonth')}
                    />
                    <span className="app-date-picker-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                        <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="col-md-2 statements-filter-reset-col">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="dashboard-wire-action-btn dashboard-recent-utility-btn dashboard-recent-reset-btn"
                    onClick={() => {
                      const defaultMonth = normalizeMonthStart(getPreviousMonth());
                      setSelectedMonth(defaultMonth);
                    }}
                    disabled={loading}
                  >
                    {t('reset')}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

        {!compact && (
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <Card className="statements-kpi-card h-100">
                <Card.Body>
                  <div className="statements-kpi-label">{t('interestGain')}</div>
                  <div className="statements-kpi-value text-success">{formatCurrency(savingsInterestGain)}</div>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="statements-kpi-card h-100">
                <Card.Body>
                  <div className="statements-kpi-label">{t('totalIncome')}</div>
                  <div className="statements-kpi-value text-success">{formatCurrency(savingsIncome)}</div>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="statements-kpi-card h-100">
                <Card.Body>
                  <div className="statements-kpi-label">{t('totalExpenses')}</div>
                  <div className="statements-kpi-value text-danger">{formatCurrency(savingsExpenses)}</div>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="statements-kpi-card h-100">
                <Card.Body>
                  <div className="statements-kpi-label">{t('netMovement')}</div>
                  <div className="statements-kpi-value">{formatCurrency(savingsIncome + savingsExpenses)}</div>
                </Card.Body>
              </Card>
            </div>
          </div>
        )}

          <Card className="statements-section-card statements-grid-panel dashboard-card mb-4">
            <Card.Body>
              <div className="statements-section-header statements-grid-panel-header">
                <h5 className="mb-0 statements-grid-panel-title">{t('trustAccountTransactions')}</h5>
                <span className="statements-section-count statements-grid-panel-count">{savingsTransactions.length}</span>
              </div>
              <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                <Table className="dashboard-table mb-0">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('description')}</th>
                      <th className="text-end">{t('amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savingsTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center text-muted">{t('noPaymentsFound')}</td>
                      </tr>
                    ) : savingsTransactions
                      .slice()
                      .sort((first, second) => new Date(second.paymentDate).getTime() - new Date(first.paymentDate).getTime())
                      .map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.paymentDate)}</td>
                          <td>{payment.description}</td>
                          <td className={`text-end ${payment.amount >= 0 ? 'text-success' : 'text-danger'}`}>{formatCurrency(payment.amount)}</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }

  return (
    <Container className={`py-5 statements-page${compact ? ' statements-page-compact' : ''}`} aria-busy={loading || sendingSelected || sendingStatements.size > 0}>
      {!compact && (
        <div className="statements-hero page-header-panel">
          <div className="statements-header d-flex justify-content-between align-items-center">
            <div>
              <h1 className="mb-1 page-header-title">
                <span className="page-header-title-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                    <path d="M7 3.5h7.5L19.5 8v10A2.5 2.5 0 0 1 17 20.5H7A2.5 2.5 0 0 1 4.5 18V6A2.5 2.5 0 0 1 7 3.5z" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
                    <path d="M14.5 3.8V8h4.2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
                    <path d="M8 11.2h8M8 14.5h8M8 17.8h5.3" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                  </svg>
                </span>
                {t('statements')}
              </h1>
            </div>
          </div>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {feedback && (
        <Alert variant={feedback.variant} onClose={() => setFeedback(null)} dismissible>
          {feedback.message}
        </Alert>
      )}

      <div className="statements-tab-panel">
        <Card className="statements-filter-card dashboard-card dashboard-filter-panel mb-4">
          <Card.Body>
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label">{t('accountHolder')}</label>
                <div className="app-dropdown-arrow-wrapper">
                  <select
                    className="form-select"
                    value={filterAccountId || ''}
                    onChange={(e) => setFilterAccountId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    disabled={loading}
                  >
                    <option value="">{t('allAccountHolders')}</option>
                    {accountHolders.map((holder) => (
                      <option key={holder.id} value={holder.id}>
                        {holder.name}
                      </option>
                    ))}
                  </select>
                  <DropdownArrow />
                </div>
              </div>
              <div className="col-md-5">
                <label className="form-label">{t('date')}</label>
                <div className="app-date-picker-shell">
                  <DatePicker
                    selected={selectedMonth}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setSelectedAccountHolderId(null);
                      setSelectedMonth(normalizeMonthStart(date));
                    }}
                    onChangeRaw={(e) => e.preventDefault()}
                    onKeyDown={(e) => e.preventDefault()}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    showFullMonthYearPicker
                    locale={language === 'af' ? 'af' : 'en-GB'}
                    className="form-control"
                    disabled={loading}
                    placeholderText={t('selectMonth')}
                  />
                  <span className="app-date-picker-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                      <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="col-md-2 statements-filter-reset-col">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="dashboard-wire-action-btn dashboard-recent-utility-btn dashboard-recent-reset-btn"
                  onClick={() => {
                    const defaultMonth = normalizeMonthStart(getPreviousMonth());
                    setSelectedAccountHolderId(null);
                    setPendingPrint(false);
                    setSelectedMonth(defaultMonth);
                  }}
                  disabled={loading}
                >
                  {t('reset')}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {(selectedHolderStatement || selectedHolderStatementsForPrint.length > 0) && (
          <div className="statements-print-only" aria-hidden="true">
            {(selectedHolderStatementsForPrint.length > 0
              ? selectedHolderStatementsForPrint
              : selectedHolderStatement
                ? [selectedHolderStatement]
                : []).map((statement) => (
                  <Card className="statements-print-preview-host mt-3" key={`print-${statement.accountHolderId}`}>
                    <Card.Body>
                      <div className="statements-print-preview-sheet">
                        <div className="statements-print-preview-head">
                          <div>
                            <div className="statements-detail-title">{t('statementDetails')}</div>
                            <div className="statements-detail-subtitle">{t('period')}: {formatMonthYear(debouncedSelectedMonth)}</div>
                          </div>
                          <div className="statements-detail-recipient">
                            <div className="statements-detail-recipient-name">{statement.accountHolderName}</div>
                            <div className="statements-detail-recipient-email">{statement.accountHolderEmail}</div>
                          </div>
                        </div>
                        <div className="statements-detail-section-title mt-3">{t('bankDetails')}</div>
                        <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                          <Table className="dashboard-table mb-0 statements-print-bank-table">
                            <tbody>
                              <tr>
                                <td>{t('accountName')}</td>
                                <td>{selectedTrustAccount?.accountName || '-'}</td>
                              </tr>
                              <tr>
                                <td>{t('bankDetails')}</td>
                                <td>{`${selectedTrustAccount?.bankName || 'ABSA'} - ${selectedTrustAccount?.branchCode || '632005'}`}</td>
                              </tr>
                              <tr>
                                <td>{t('accountType')}</td>
                                <td>{`${getDisplayAccountType(selectedTrustAccount?.accountKind, t)} - ${selectedTrustAccount?.accountNumber || '-'}`}</td>
                              </tr>
                              <tr>
                                <td>{t('paymentReference')}</td>
                                <td>{t('paymentReferenceDescription')}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                        <div className="statements-detail-section-title mt-3">{t('accountOverviewSection')}</div>
                        <div className="statements-print-overview-grid">
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('requiredContribution')}</div>
                            <div className="statements-print-overview-value">{formatCurrency(statement.requiredContribution)}</div>
                          </div>
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('monthlyAccountFees')}</div>
                            <div className="statements-print-overview-value">{formatCurrency(statement.monthlyAccountFees)}</div>
                          </div>
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('totalRequiredContribution')}</div>
                            <div className="statements-print-overview-value">{formatCurrency(statement.totalRequiredContribution)}</div>
                          </div>
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('notes')}</div>
                            <div className="statements-print-overview-value text-success">{formatCurrency(statement.holderCredits)}</div>
                          </div>
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('totalDebits')}</div>
                            <div className="statements-print-overview-value text-danger">{formatCurrency(statement.totalDebits)}</div>
                          </div>
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('outstandingFees')}</div>
                            <div className="statements-print-overview-value text-danger">{formatCurrency(statement.outstandingFees)}</div>
                          </div>
                          <div className="statements-print-overview-item">
                            <div className="statements-print-overview-label">{t('overpaidAmount')}</div>
                            <div className="statements-print-overview-value text-success">{formatCurrency(statement.overpaidAmount)}</div>
                          </div>
                        </div>
                        <div className="statements-detail-section-title mt-3">{t('expenseSummarySection')}</div>
                        <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap statements-detail-table">
                          <Table className="dashboard-table mb-0 statements-print-summary-table">
                            <thead>
                              <tr>
                                <th>{t('farmWork')}</th>
                                <th>{t('description')}</th>
                                <th className="text-end">{t('requiredContribution')}</th>
                                <th className="text-end">{t('appliedToFarmWork')}</th>
                                <th className="text-end">{t('outstandingFees')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(holderAllocationMap.allocationsByHolder.get(statement.accountHolderId) || []).map((allocation) => (
                                <tr key={`preview-allocation-${statement.accountHolderId}-${allocation.expenseId}`}>
                                  <td>{allocation.expenseTypeName || '-'}</td>
                                  <td>{allocation.description || '-'}</td>
                                  <td className="text-end">{formatCurrency(allocation.requiredContribution)}</td>
                                  <td className="text-end">{formatCurrency(allocation.allocatedPaid)}</td>
                                  <td className="text-end">{formatCurrency(allocation.outstandingContribution)}</td>
                                </tr>
                              ))}
                              {(holderAllocationMap.allocationsByHolder.get(statement.accountHolderId) || []).length === 0 && (
                                <tr>
                                  <td colSpan={5} className="text-center text-muted">{t('noFarmWorkForSelectedMonth')}</td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                        <div className="statements-detail-section-title mt-3">{t('accountFeeBreakdown')}</div>
                        <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                          <Table className="dashboard-table mb-0">
                            <thead>
                              <tr>
                                <th>{t('address')}</th>
                                <th className="text-end">{t('monthlyFee')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statement.addressFees.map((item, index) => (
                                <tr key={`preview-fee-${statement.accountHolderId}-${index}`}>
                                  <td>{item.address}</td>
                                  <td className="text-end">{formatCurrency(item.monthlyFee)}</td>
                                </tr>
                              ))}
                              {statement.addressFees.length === 0 && (
                                <tr>
                                  <td colSpan={2} className="text-center text-muted">{t('noAddressesAssigned')}</td>
                                </tr>
                              )}
                              <tr>
                                <td className="fw-semibold">{t('monthlyAccountFees')}</td>
                                <td className="text-end fw-semibold">{formatCurrency(statement.monthlyAccountFees)}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                        <div className="statements-detail-section-title mt-3">{t('detailedTransactions')}</div>
                        <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                          <Table className="dashboard-table mb-0">
                            <thead>
                              <tr>
                                <th>{t('date')}</th>
                                <th>{t('description')}</th>
                                <th className="text-end">{t('amount')}</th>
                                <th>{t('trustAccount')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(statement.paymentSummary?.payments || []).map((payment) => (
                                <tr key={`preview-${statement.accountHolderId}-${payment.paymentId}`}>
                                  <td>{formatDate(payment.paymentDate)}</td>
                                  <td>{payment.description}</td>
                                  <td className="text-end">{formatCurrency(payment.amount)}</td>
                                  <td>{payment.trustAccountName || '-'}</td>
                                </tr>
                              ))}
                              {(!statement.paymentSummary || statement.paymentSummary.payments.length === 0) && (
                                <tr>
                                  <td colSpan={4} className="text-center text-muted">{t('noPaymentsFound')}</td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
          </div>
        )}

        <>
            {loading && <div className="mb-3 d-flex justify-content-center"><LoadingIndicator label={t('loading')} subtle /></div>}

            {!compact && (
              <>
                <div className="statements-selected-month mb-3">
                  {t('showing')}: {selectedAccountHolderLabel} • {formatMonthYear(debouncedSelectedMonth)}
                </div>
                <div className="statements-allocation-note mb-3 text-muted small">
                  <strong>{t('allocationMethodLabel')}:</strong> {t('allocationMethodOldestFirst')} {t('includesMonthlyAccountFees')}.
                </div>
                <div className="row g-3 mb-4">
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card statements-kpi-card-required h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('requiredContribution')}</div>
                        <div className="statements-kpi-value">{formatCurrency(holderSummaryTotals.totalRequiredContribution)}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('monthlyAccountFees')}</div>
                        <div className="statements-kpi-value">{formatCurrency(holderSummaryTotals.monthlyAccountFees)}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card statements-kpi-card-applied h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('appliedToFarmWork')}</div>
                        <div className="statements-kpi-value text-success">{formatCurrency(holderSummaryTotals.allocatedToFarmWork)}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('notes')}</div>
                        <div className="statements-kpi-value text-success">{formatCurrency(holderSummaryTotals.holderCredits)}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('totalDebits')}</div>
                        <div className="statements-kpi-value text-danger">{formatCurrency(holderSummaryTotals.totalDebits)}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card statements-kpi-card-outstanding h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('outstandingFees')}</div>
                        <div className="statements-kpi-value text-danger">{formatCurrency(holderSummaryTotals.outstandingFees)}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="col-md-6 col-xl-3">
                    <Card className="statements-kpi-card statements-kpi-card-status h-100">
                      <Card.Body>
                        <div className="statements-kpi-label">{t('contributorsStatus')}</div>
                        <div className="d-flex gap-2 flex-wrap">
                          <Badge bg="danger">{t('notPaid')}: {holderSummaryTotals.notPaid}</Badge>
                          <Badge bg="success">{t('paidInFull')}: {holderSummaryTotals.paidInFull}</Badge>
                          <Badge bg="warning" text="dark">{t('withOutstanding')}: {holderSummaryTotals.withOutstanding}</Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </div>

                <Card className="statements-section-card statements-grid-panel dashboard-card mb-4">
                  <Card.Body>
                    <div className="statements-section-header statements-grid-panel-header">
                      <h5 className="mb-0 statements-grid-panel-title">{t('farmWorkMonthlyOverview')}</h5>
                      <span className="statements-section-count statements-grid-panel-count">{expenseOverviewRows.length}</span>
                    </div>

                    {!loading && expenseOverviewRows.length > 0 ? (
                      <>
                        <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                          <Table className="dashboard-table mb-0">
                            <thead>
                              <tr>
                                <th>{t('farmWork')}</th>
                                <th>{t('contributors')}</th>
                                <th className="text-end">{t('cost')}</th>
                                <th className="text-end">{t('requiredContribution')}</th>
                                <th className="text-end">{t('appliedToFarmWork')}</th>
                                <th className="text-end">{t('outstandingFees')}</th>
                                <th className="text-end">{t('costCoverageGap')}</th>
                                <th>{t('status')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedExpenseOverviewRows.map((row) => (
                                <tr key={row.expenseId}>
                                  <td>
                                    <div className="fw-semibold">{row.expenseTypeName}</div>
                                    <small className="text-muted statements-grid-subtext">{row.description || '-'}</small>
                                  </td>
                                  <td>
                                    <div className="fw-semibold">{row.contributorsCount}</div>
                                    <div className="statements-contributor-breakdown">
                                      <span className="text-danger">{t('notPaid')}: {row.unpaidContributors}</span>
                                      <span className="text-warning">{t('partiallyPaid')}: {row.partiallyPaidContributors}</span>
                                      <span className="text-success">{t('paidInFull')}: {row.paidInFullContributors}</span>
                                    </div>
                                  </td>
                                  <td className="text-end">{formatCurrency(row.cost)}</td>
                                  <td className="text-end">{formatCurrency(row.expectedContribution)}</td>
                                  <td className="text-end">{formatCurrency(row.estimatedPaid)}</td>
                                  <td className={`text-end fw-semibold ${row.outstandingContribution > 0 ? 'text-danger' : 'text-success'}`}>
                                    {formatCurrency(row.outstandingContribution)}
                                  </td>
                                  <td className={`text-end fw-semibold ${row.costCoverageGap > 0 ? 'text-warning' : 'text-success'}`}>
                                    {formatCurrency(row.costCoverageGap)}
                                  </td>
                                  <td>
                                    {row.costCoverageGap > 0 ? (
                                      <Badge bg="danger" className="statements-badge-sm">{t('outstanding')}</Badge>
                                    ) : (
                                      <Badge bg="success" className="statements-badge-sm">{t('paidInFull')}</Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        <div className="dashboard-table-footer d-flex justify-content-between align-items-center mt-2">
                          <div className="dashboard-table-meta text-muted">
                            {t('showing')} {expenseOverviewPageStart}-{expenseOverviewPageEnd} {t('of')} {expenseOverviewRows.length}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                            <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                              <Form.Select
                                className="dashboard-rows-select"
                                value={expenseOverviewPageSize}
                                onChange={(e) => setExpenseOverviewPageSize(parseInt(e.target.value, 10))}
                                disabled={loading}
                              >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                              </Form.Select>
                              <DropdownArrow />
                            </div>
                            <Pagination className="mb-0 dashboard-pagination">
                              <Pagination.First onClick={() => setExpenseOverviewCurrentPage(1)} disabled={safeExpenseOverviewCurrentPage === 1 || loading} />
                              <Pagination.Prev onClick={() => setExpenseOverviewCurrentPage((page) => Math.max(1, page - 1))} disabled={safeExpenseOverviewCurrentPage === 1 || loading} />
                              <Pagination.Item active>{safeExpenseOverviewCurrentPage}</Pagination.Item>
                              {expenseOverviewTotalPages > 1 && <Pagination.Item disabled>/ {expenseOverviewTotalPages}</Pagination.Item>}
                              <Pagination.Next
                                onClick={() => setExpenseOverviewCurrentPage((page) => Math.min(expenseOverviewTotalPages, page + 1))}
                                disabled={safeExpenseOverviewCurrentPage === expenseOverviewTotalPages || loading}
                              />
                              <Pagination.Last onClick={() => setExpenseOverviewCurrentPage(expenseOverviewTotalPages)} disabled={safeExpenseOverviewCurrentPage === expenseOverviewTotalPages || loading} />
                            </Pagination>
                          </div>
                        </div>
                      </>
                    ) : loading ? (
                      <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                        <Table className="dashboard-table mb-0">
                          <thead>
                            <tr>
                              <th>{t('farmWork')}</th>
                              <th>{t('contributors')}</th>
                              <th className="text-end">{t('cost')}</th>
                              <th className="text-end">{t('requiredContribution')}</th>
                              <th className="text-end">{t('appliedToFarmWork')}</th>
                              <th className="text-end">{t('outstandingFees')}</th>
                              <th className="text-end">{t('costCoverageGap')}</th>
                              <th>{t('status')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan={8} className="text-center py-4 text-muted">
                                <div className="dashboard-grid-loading-message">
                                  <LoadingIndicator label={t('loading')} subtle />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="statements-empty">{t('noFarmWorkForSelectedMonth')}</div>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}

            {!loading && holderStatementRows.length === 0 ? (
              <Alert variant="info">{t('noHolderContributionOrPaymentsForMonth')} {formatMonthYear(debouncedSelectedMonth)}</Alert>
            ) : (
              <Card className="statements-section-card statements-grid-panel dashboard-card mb-4">
                <Card.Body>
                  <div className="statements-section-header statements-grid-panel-header">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h5 className="mb-0 statements-grid-panel-title">{t('farmWorkContributionOverview')}</h5>
                      <span className="statements-section-count statements-grid-panel-count">{holderStatementRows.length}</span>
                    </div>
                    {selectedContributorCount > 0 && (
                      <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end ms-auto">
                        <span className="text-muted small">{t('selectedContributorsCount')}: {selectedContributorCount}</span>
                        <Button
                          variant="outline-info"
                          className="statements-toolbar-btn d-inline-flex align-items-center gap-2"
                          onClick={handlePrintSelectedStatements}
                          disabled={sendingStatements.size > 0}
                        >
                          {t('print')}
                        </Button>
                        <Button
                          variant="primary"
                          className="statements-toolbar-btn statements-primary-action d-inline-flex align-items-center gap-2"
                          onClick={() => setShowSendSelectedConfirm(true)}
                          disabled={sendingStatements.size > 0}
                        >
                          {sendingStatements.size > 0 && <LoadingIndicator label={t('sendingStatements')} inline compact showLines={false} />}
                          {sendingStatements.size > 0 ? t('sendingStatements') : t('sendSelectedEmails')}
                        </Button>
                      </div>
                    )}
                  </div>

                  {!loading && holderStatementRows.length > 0 ? (
                    <>
                      <div className="table-responsive statements-table-wrapper statements-contribution-wrapper dashboard-wire-table-wrap">
                        <Table className="dashboard-table statements-contribution-table mb-0">
                          <thead>
                            <tr>
                              <th className="statements-select-col text-center">
                                <input
                                  ref={selectAllContributorsRef}
                                  type="checkbox"
                                  className="form-check-input"
                                  aria-label={t('selectAllContributors')}
                                  checked={areAllFilteredContributorsSelected}
                                  disabled={holderStatementRows.length === 0 || sendingStatements.size > 0}
                                  onChange={(e) => handleToggleSelectAllContributors(e.target.checked)}
                                />
                              </th>
                              <th>{t('accountHolder')}</th>
                              <th className="text-end">{t('requiredContribution')}</th>
                              <th className="text-end">{t('monthlyAccountFees')}</th>
                              <th className="text-end">{t('creditsVsDebits')}</th>
                              <th className="text-end">{t('appliedToFarmWork')}</th>
                              <th className="text-end">{t('outstandingFees')}</th>
                              <th>{t('status')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedHolderOverviewRows.map((row) => (
                              <tr key={row.accountHolderId}>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    aria-label={`${t('selectContributor')} ${row.accountHolderName}`}
                                    checked={selectedContributorIds.has(row.accountHolderId)}
                                    disabled={sendingStatements.size > 0}
                                    onChange={(e) => handleContributorSelection(row.accountHolderId, e.target.checked)}
                                  />
                                </td>
                                <td>
                                  <div className="statements-holder-name">{row.accountHolderName}</div>
                                  <div className="statements-holder-email statements-grid-subtext">{row.accountHolderEmail}</div>
                                  <div className="statements-grid-subtext text-muted">{row.contributionItemCount} {t('farmWorkItems').toLowerCase()}</div>
                                  {row.addressFees.length > 0 && (
                                    <div className="statements-grid-subtext text-muted mt-1">
                                      {t('accountFeeBreakdown')}: {row.addressFees.map((item) => `${item.address} (${formatCurrency(item.monthlyFee)})`).join(' • ')}
                                    </div>
                                  )}
                                  {row.holderCredits > 0 && (
                                    <div className="statements-grid-subtext text-muted mt-1">
                                      {t('notes')}: {formatCurrency(row.holderCredits)}
                                    </div>
                                  )}
                                </td>
                                <td className="text-end">{formatCurrency(row.requiredContribution)}</td>
                                <td className="text-end">{formatCurrency(row.monthlyAccountFees)}</td>
                                <td className="text-end">
                                  <div className="statements-credits-vs-debits-cell text-success">{formatCurrency(row.holderCredits)}</div>
                                  <div className="statements-credits-vs-debits-cell text-danger">{formatCurrency(row.totalDebits)}</div>
                                </td>
                                <td className="text-end">{formatCurrency(row.allocatedToFarmWork)}</td>
                                <td className={`text-end fw-bold ${row.outstandingFees > 0 ? 'text-danger' : row.overpaidAmount > 0 ? '' : 'text-success'}`}>
                                  <div>{formatCurrency(row.outstandingFees)}</div>
                                  {row.overpaidAmount > 0 && (
                                    <span className="statements-overpaid-chip d-block mt-1">
                                      {t('overpaidShort')}: {formatCurrency(row.overpaidAmount)}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {row.requiredContribution <= 0 ? (
                                    <Badge bg="secondary" className="statements-badge-sm">{t('noContributionDue')}</Badge>
                                  ) : row.holderCredits <= 0 ? (
                                    <Badge bg="danger" className="statements-badge-sm">{t('notPaid')}</Badge>
                                  ) : row.outstandingFees > 0 ? (
                                    <Badge bg="warning" text="dark" className="statements-badge-sm">{t('partiallyPaid')}</Badge>
                                  ) : row.overpaidAmount > 0 ? (
                                    <Badge bg="info" className="statements-badge-sm">{t('overpaidAmount')}</Badge>
                                  ) : (
                                    <Badge bg="success" className="statements-badge-sm">{t('paidInFull')}</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <div className="dashboard-table-footer d-flex justify-content-between align-items-center mt-2">
                        <div className="dashboard-table-meta text-muted">
                          {t('showing')} {holderOverviewPageStart}-{holderOverviewPageEnd} {t('of')} {holderStatementRows.length}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                          <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                            <Form.Select
                              className="dashboard-rows-select"
                              value={holderOverviewPageSize}
                              onChange={(e) => setHolderOverviewPageSize(parseInt(e.target.value, 10))}
                              disabled={loading}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </Form.Select>
                            <DropdownArrow />
                          </div>
                          <Pagination className="mb-0 dashboard-pagination">
                            <Pagination.First onClick={() => setHolderOverviewCurrentPage(1)} disabled={safeHolderOverviewCurrentPage === 1 || loading} />
                            <Pagination.Prev onClick={() => setHolderOverviewCurrentPage((page) => Math.max(1, page - 1))} disabled={safeHolderOverviewCurrentPage === 1 || loading} />
                            <Pagination.Item active>{safeHolderOverviewCurrentPage}</Pagination.Item>
                            {holderOverviewTotalPages > 1 && <Pagination.Item disabled>/ {holderOverviewTotalPages}</Pagination.Item>}
                            <Pagination.Next
                              onClick={() => setHolderOverviewCurrentPage((page) => Math.min(holderOverviewTotalPages, page + 1))}
                              disabled={safeHolderOverviewCurrentPage === holderOverviewTotalPages || loading}
                            />
                            <Pagination.Last onClick={() => setHolderOverviewCurrentPage(holderOverviewTotalPages)} disabled={safeHolderOverviewCurrentPage === holderOverviewTotalPages || loading} />
                          </Pagination>
                        </div>
                      </div>
                    </>
                  ) : loading ? (
                    <div className="table-responsive statements-table-wrapper dashboard-wire-table-wrap">
                      <Table className="dashboard-table mb-0">
                        <thead>
                          <tr>
                            <th className="statements-select-col" />
                            <th>{t('accountHolder')}</th>
                            <th className="text-end">{t('requiredContribution')}</th>
                            <th className="text-end">{t('monthlyAccountFees')}</th>
                            <th className="text-end">{t('creditsVsDebits')}</th>
                            <th className="text-end">{t('appliedToFarmWork')}</th>
                            <th className="text-end">{t('outstandingFees')}</th>
                            <th>{t('status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td colSpan={8} className="text-center py-4 text-muted">
                              <div className="dashboard-grid-loading-message">
                                <LoadingIndicator label={t('loading')} subtle />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="statements-empty">{t('noAccountHolderSummaryForFilters')}</div>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        
      </div>

      <ConfirmationModal
        show={showSendSelectedConfirm}
        title={t('sendSelectedEmails')}
        message={t('confirmSendSelectedStatements')}
        onConfirm={handleSendSelectedStatements}
        onCancel={() => {
          if (!sendingSelected) {
            setShowSendSelectedConfirm(false);
          }
        }}
        variant="info"
        confirmText={t('sendEmail')}
        isLoading={sendingSelected}
      />
    </Container>
  );
};

export default Statements;

